import type { ThemeMeta, ThemeTokens } from '@/previewThemes/meta';

/**
 * Lumera's design constants.
 *
 * A skincare house: cream and blush grounds, a cocoa primary, a high-contrast
 * serif for every heading and a quiet geometric sans under it. The chrome is a
 * rounded masthead floating on the page's own colour, and the page below it is
 * mostly photograph - the look a beauty brand is bought on before a single
 * ingredient is read.
 *
 * The shop it is previewed with is a beauty and personal-care store stocked
 * with the platform's own Beauty & Cosmetics library pictures - see
 * `catalog.ts` for why it ships its own, and `photos.ts` for where each
 * picture came from.
 */
export const tokens: ThemeTokens = {
  // A display serif with long, fine hairlines. It only reads as luxurious at
  // size, which is why the section headings are centred and given air rather
  // than packed into a row with a link beside them.
  headingFont: 'Cormorant Garamond',
  // A warmer, lighter cocoa than the primary: the sale line, the in-stock note
  // and the wordmark's mark are painted with it, so a detail reads as the same
  // family as the buttons without competing with them.
  accent: '#b07a52',
  monoFont: null,
  headingTracking: '0',
  headingTransform: 'none',
  // Every section heading stands over its section, centred - the way a beauty
  // brand announces a shelf rather than labelling one.
  headingAlign: 'center',
  // 1280px: narrower than a marketplace, wide enough for four product cards
  // beside each other on the home page's product row.
  containerMax: '80rem',
  // Generous. White space is most of what this look is made of.
  spaceScale: 1.2,
  // The announcement rail plus the floating masthead and its margin.
  headerHeight: '8.5rem',
};

export const meta: ThemeMeta = {
  label: 'Lumera',
  hint: 'A skincare house: cream and cocoa, a floating rounded masthead and a serif that whispers.',
  industries: ['beauty_cosmetics', 'salon_beauty_services'],
  look: {
    colors: {
      primary: '#7a5238',
      background: '#fdf9f5',
      surface: '#f5ebe2',
      text: '#2b211c',
      muted: '#8b7b70',
    },
    typography: {
      font_family: 'Montserrat',
      heading_font: 'Cormorant Garamond',
      // Cormorant is drawn thin; 500 is the weight where its hairlines still
      // hold on a phone without the face turning heavy on a laptop.
      heading_weight: '500',
    },
    // The pill. 32 is the most the schema allows, and on the 44-48px
    // controls this theme draws it rounds every button fully.
    buttons: { radius_px: 32, style: 'solid' },
    // No `catalog` named: this theme ships its own in `catalog.ts`, stocked
    // with photographs rather than the drawn beauty shop.
  },
};
